import { Box, Button, Typography } from '@mui/material';

import { tokens } from '../../../theme-next';

interface ParcoursBannerProps {
  parcours: string | null;
  /**
   * Provenance du parcours, affichée sous le libellé : « déclaré par l'agence à
   * l'étape 1 » vs « déterminé par l'analyse ». Sans elle, un bandeau
   * « Succession » se lit comme un verdict de l'IA alors qu'il peut n'être
   * qu'une case cochée (cf. GD-567-EJ / #13374, remonté par François).
   */
  details?: string | null;
  /**
   * Fourni UNIQUEMENT quand le parcours vient de la déclaration agence : affiche
   * un bouton de retour à l'étape 1 pour décocher une case posée par erreur.
   * Absent côté review (le god ne modifie pas la déclaration de l'agence).
   */
  onCorrectDeclaration?: () => void;
  /**
   * Rideau : tant que l'analyse OCR/moteur tourne, le parcours peut basculer
   * transitoirement (ex. « Parcours A » détecté par le fallback regex puis retour
   * à « classique » quand v1/Opus résolvent). On masque donc le verdict de parcours
   * jusqu'à ce que l'analyse soit finie — exactement comme le bloc Alertes.
   */
  isAnalyzing?: boolean;
}

/**
 * Banner haut indiquant quel parcours métier est en cours.
 *
 * `parcours` vient du store (alimenté par la détection Parcours A : vendeur
 * professionnel sur la cession + C.1 carte grise particulier). Tant qu'aucun
 * parcours alternatif n'est détecté, on affiche l'état "classique" en vert ;
 * dès qu'un parcours alternatif est actif, le badge le reflète au lieu du
 * libellé figé "non activés".
 */
export const isParcoursAlternatif = (parcours: string | null): boolean => {
  if (!parcours) return false;
  const p = parcours.toLowerCase();
  return p.includes('parcours a') || p.includes('parcours_a') || p.includes('succession');
};

export const ParcoursBanner = ({
  parcours,
  details,
  onCorrectDeclaration,
  isAnalyzing = false,
}: ParcoursBannerProps) => {
  // Rideau pendant l'analyse : état neutre, on ne montre pas de parcours transitoire.
  if (isAnalyzing) {
    return (
      <Box
        sx={{
          backgroundColor: tokens.color.surface,
          border: `0.5px solid ${tokens.color.border}`,
          borderLeft: `4px solid ${tokens.color.textMuted}`,
          borderRadius: `${tokens.radius.lg}px`,
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700, color: tokens.color.textMuted }}>
          Analyse du parcours en cours…
        </Typography>
      </Box>
    );
  }
  const alternatif = isParcoursAlternatif(parcours);
  return (
    <Box
      sx={{
        backgroundColor: alternatif ? tokens.color.orangeLight : tokens.color.greenLight,
        border: `0.5px solid ${alternatif ? tokens.color.orangeBorder : tokens.color.greenBorder}`,
        borderLeft: `4px solid ${alternatif ? tokens.color.orangeDark : tokens.color.green}`,
        borderRadius: `${tokens.radius.lg}px`,
        padding: '14px 18px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 280 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: alternatif ? tokens.color.orangeDark : tokens.color.greenDeep }}
        >
          {parcours ?? 'Parcours classique — aucun parcours alternatif déclenché'}
        </Typography>
        {details && (
          <Typography
            variant="caption"
            sx={{
              color: alternatif ? tokens.color.orangeDark : tokens.color.greenDeep,
              opacity: 0.85,
              display: 'block',
              marginTop: '2px',
            }}
          >
            {details}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {onCorrectDeclaration && (
          <Button
            onClick={onCorrectDeclaration}
            sx={{
              fontSize: '12px',
              fontWeight: 700,
              textTransform: 'none',
              color: tokens.color.orangeDark,
              padding: '2px 8px',
              minWidth: 0,
            }}
          >
            Corriger la déclaration
          </Button>
        )}
        <Box
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            textTransform: 'uppercase',
            color: alternatif ? '#fff' : tokens.color.orangeDark,
            backgroundColor: alternatif ? tokens.color.orangeDark : tokens.color.orangeLight,
            border: `0.5px solid ${tokens.color.orangeBorder}`,
            borderRadius: `${tokens.radius.full}px`,
            padding: '4px 10px',
            letterSpacing: '0.05em',
          }}
        >
          {alternatif ? 'Parcours alternatif activé' : 'Parcours A / B / C non activés'}
        </Box>
      </Box>
    </Box>
  );
};
